import { useState } from "react";
import CustomButton from "./CustomButton";
import { LanguageCodeType } from "const/languageCodes";
import NaverLogo from "assets/images/social_login/naver_logo.png";
import KakaoLogo from "assets/images/social_login/kakao_logo.jpeg";
import LineLogo from "assets/images/social_login/line_logo.png";
import { Grid } from "@mui/material";
import LoadingSpinner from "components/LoadingSpinner";
import { StorageUtils } from "utils/storageUtils";
import { ProviderUtils } from "utils/providerUtils";
import useLanguages from "hooks/useLanguages";
import { useIntl } from "react-intl";

/* 
  naver: #03C75A   #FFFFFF
  kakao: #FEE500 	#000000 85%
*/

const urlSubdomain = ProviderUtils.provider;

// Naver
const NAVER_CLIENT_ID = process.env.REACT_APP_NAVER_CLIENT_ID;
const NAVER_REDIRECT_URI = `https://${
  urlSubdomain
    ? `${urlSubdomain}.${process.env.REACT_APP_NAVER_REDIRECT_URI_PROVIDER}`
    : process.env.REACT_APP_NAVER_REDIRECT_URI_DEFAULT
}`;
const NAVER_LOGIN_URI = `https://nid.naver.com/oauth2.0/authorize?response_type=code&client_id=${NAVER_CLIENT_ID}&redirect_uri=${NAVER_REDIRECT_URI}`;

// Kakao
const KAKAO_CLIENT_ID = process.env.REACT_APP_KAKAO_CLIENT_ID;
const KAKAO_REDIRECT_URI = `https://${
  urlSubdomain
    ? `${urlSubdomain}.${process.env.REACT_APP_KAKAO_REDIRECT_URI_PROVIDER}`
    : process.env.REACT_APP_KAKAO_REDIRECT_URI_DEFAULT
}`;
const KAKAO_LOGIN_URI = `https://kauth.kakao.com/oauth/authorize?client_id=${KAKAO_CLIENT_ID}&redirect_uri=${KAKAO_REDIRECT_URI}&response_type=code`;

// Line
const LINE_CLIENT_ID = process.env.REACT_APP_LINE_CHANNEL_ID;
const LINE_REDIRECT_URI = `https://${
  urlSubdomain
    ? `${urlSubdomain}.${process.env.REACT_APP_LINE_REDIRECT_URI_PROVIDER}`
    : process.env.REACT_APP_LINE_REDIRECT_URI_DEFAULT
}`;
const LINE_LOGIN_URI = `https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=${LINE_CLIENT_ID}&redirect_uri=${LINE_REDIRECT_URI}&scope=profile%20openid%20email`;

// Google (using URL and URLSearchParams)
// const googleQuery = {
//   client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID!,
//   redirect_uri: `https://${urlSubdomain}${process.env.REACT_APP_GOOGLE_REDIRECT_URI}`,
//   response_type: "code",
//   scope:
//     "https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile",
// };
// const uri = new URL("https://accounts.google.com/o/oauth2/v2/auth");
// uri.search = new URLSearchParams(googleQuery).toString();
// const GOOGLE_REDIRECT_URI = uri.toString();

type SocialLoginProvider =
  | "Naver"
  | "Kakao"
  //  | "Google"
  | "Line";

const getBackgroundColor = (loginProvider: SocialLoginProvider) => {
  switch (loginProvider) {
    case "Naver":
      return "#03C75A";
    case "Kakao":
      return "#FEE500";
    case "Line":
      return "#06C755";
    // case "Google":
    //   return "#F2F2F2";
    default:
      const _exhaustiveCheck: never = loginProvider;
      return _exhaustiveCheck;
  }
};

const getTextColor = (loginProvider: SocialLoginProvider) => {
  switch (loginProvider) {
    case "Naver":
    case "Line":
      return "#FFFFFF";
    case "Kakao":
      // case "Google":
      return "#000000D9";
    default:
      const _exhaustiveCheck: never = loginProvider;
      return _exhaustiveCheck;
  }
};

const getButtonText = (
  loginProvider: SocialLoginProvider,
  language: LanguageCodeType = "ko"
) => {
  switch (loginProvider) {
    case "Naver":
      return language === "ko" ? "네이버 로그인" : "Login with Naver";
    case "Kakao":
      return language === "ko" ? "카카오 로그인" : "Login with Kakao";
    case "Line":
      return language === "ja"
        ? "LINEでログイン"
        : language === "ko"
        ? "LINE으로 로그인"
        : "Login with Line";
    // case "Google":
    //   return language === "ko" ? "구글 로그인" : "Login with Google";
    default:
      const _exhaustiveCheck: never = loginProvider;
      return _exhaustiveCheck;
  }
};

const getLogo = (loginProvider: SocialLoginProvider) => {
  switch (loginProvider) {
    case "Naver":
      return NaverLogo;
    case "Kakao":
      return KakaoLogo;
    case "Line":
      return LineLogo;
    // case "Google":
    //   return GoogleLogo;
    default:
      const _exhaustiveCheck: never = loginProvider;
      return _exhaustiveCheck;
  }
};

const getLogoHeight = (loginProvider: SocialLoginProvider) => {
  switch (loginProvider) {
    case "Naver":
      return 40;
    case "Kakao":
      return 34;
    case "Line":
      return 30;
    // case "Google":
    //   return 36;
    default:
      const _exhaustiveCheck: never = loginProvider;
      return _exhaustiveCheck;
  }
};

const getLoginUri = (loginProvider: SocialLoginProvider, state?: string) => {
  switch (loginProvider) {
    case "Naver":
      return `${NAVER_LOGIN_URI}&state=${state}`;
    case "Kakao":
      return KAKAO_LOGIN_URI;
    case "Line":
      return `${LINE_LOGIN_URI}&state=${state}`;
    // case "Google":
    // return GOOGLE_REDIRECT_URI;
    default:
      const _exhaustiveCheck: never = loginProvider;
      return _exhaustiveCheck;
  }
};

interface SocialLoginButtonProps {
  loginProvider: SocialLoginProvider;
  closeModal?: () => void;
}

const SocialLoginButton = ({
  loginProvider,
  closeModal,
}: SocialLoginButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const { locale } = useIntl() as { locale: LanguageCodeType };
  const { isKorean } = useLanguages();

  const handleClick = () => {
    setIsLoading(true);
    StorageUtils.IsLoginModal.set(Boolean(closeModal));
    const state = crypto.getRandomValues(new Uint16Array(1)).join("");
    window.location.href = getLoginUri(loginProvider, state);
    setIsLoading(false);
  };

  return (
    <>
      <CustomButton
        style={{
          backgroundColor: getBackgroundColor(loginProvider),
          color: getTextColor(loginProvider),
          textTransform: "none",
        }}
        onClick={handleClick}
      >
        <Grid container alignItems="center" sx={{ width: "80%" }} spacing={1}>
          <Grid item xs={isKorean ? 4 : 3.5} sx={{ textAlign: "center" }}>
            <img
              src={getLogo(loginProvider)}
              alt={`${loginProvider} Icon`}
              style={{
                height: `${getLogoHeight(loginProvider)}px`,
                objectFit: "contain",
              }}
            />
          </Grid>
          <Grid
            item
            xs={isKorean ? 8 : 8.5}
            sx={{ textAlign: "start", fontWeight: "600" }}
          >
            {getButtonText(loginProvider, locale)}
          </Grid>
        </Grid>
      </CustomButton>
      {isLoading && <LoadingSpinner overlap />}
    </>
  );
};

export default SocialLoginButton;
